import { DateTime } from 'luxon';
import { useMemo } from 'react';

/** Hook to check if a location is currently open using the same logic as OpeningHours component */
export const useIsLocationOpen = (
  openingHours: any,
  openingHoursExceptions: any[],
  timezone: string,
  node_locale: string,
): boolean => {
  return useMemo(() => {
    if (!openingHours || !node_locale || !timezone) return false;

    const todayDateTimeInMeetingplaceTimezone = DateTime.now()
      .setLocale(node_locale)
      .setZone(timezone);

    const todaysWeekdayNumber =
      Number(todayDateTimeInMeetingplaceTimezone.weekday.toFixed()) - 1;

    // Check if today matches an exception
    const todaysException = openingHoursExceptions?.find(
      (exceptionDate: any) => {
        const exceptionDateTime = DateTime.fromISO(
          exceptionDate.date,
        ).setLocale(node_locale);

        return (
          exceptionDateTime.hasSame(
            todayDateTimeInMeetingplaceTimezone,
            'month',
          ) &&
          exceptionDateTime.hasSame(todayDateTimeInMeetingplaceTimezone, 'day')
        );
      },
    );

    // If today is a closed exception, location is not open
    if (todaysException) {
      const isClosed =
        todaysException.closes === 'Closed' ||
        todaysException.opens === 'Closed';
      if (isClosed) {
        return false;
      }
      // For exception with special hours, check if current time is within those hours
      return isTimeWithinHours(
        todayDateTimeInMeetingplaceTimezone.hour,
        todayDateTimeInMeetingplaceTimezone.minute,
        todaysException.opens,
        todaysException.closes,
      );
    }

    // Get today's regular opening hours
    const hoursMap = [
      { open: 'mondayOpen', close: 'mondayClose' },
      { open: 'tuesdayOpen', close: 'tuesdayClose' },
      { open: 'wednesdayOpen', close: 'wednesdayClose' },
      { open: 'thursdayOpen', close: 'thursdayClose' },
      { open: 'fridayOpen', close: 'fridayClose' },
      { open: 'saturdayOpen', close: 'saturdayClose' },
      { open: 'sundayOpen', close: 'sundayClose' },
    ];

    const todaysHours = hoursMap[todaysWeekdayNumber];
    const openTimeStr = openingHours[todaysHours.open];
    const closeTimeStr = openingHours[todaysHours.close];

    // If marked as closed for this day
    if (openTimeStr === 'Closed' || closeTimeStr === 'Closed') {
      return false;
    }

    // Check if current time is within opening hours
    return isTimeWithinHours(
      todayDateTimeInMeetingplaceTimezone.hour,
      todayDateTimeInMeetingplaceTimezone.minute,
      openTimeStr,
      closeTimeStr,
    );
  }, [openingHours, openingHoursExceptions, timezone, node_locale]);
};

/** Helper to check if a given time is within opening hours returns true if current time is within the hours */
function isTimeWithinHours(
  currentHour: number,
  currentMinute: number,
  openTimeStr: string,
  closeTimeStr: string,
): boolean {
  const openTime = openTimeStr.split(':');
  const closeTime = closeTimeStr.split(':');

  const openMinutes = parseInt(openTime[0]) * 60 + parseInt(openTime[1]);
  const closeMinutes = parseInt(closeTime[0]) * 60 + parseInt(closeTime[1]);
  const currentMinutes = currentHour * 60 + currentMinute;

  return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
}
